// Customer Service -> Comps -> "Download" workbook (2026-10-06).
//
// Three sheets, built from the same payload the screen shows (the selected
// season, the "Matched users only" filter applied as on screen):
//   Summary     the season's totals: people by status (Employee / Volunteer /
//               Associate / Unknown), comps and value; the valuation basis.
//   Sessions    every session (train) that carried a comp, ranked by comps.
//   Users       the screen table, one row per person, with each person's
//               individual comps as an outlined (collapsed) group beneath and a
//               subtotal line - click the + in Excel to drill down.
// exceljs is dynamically imported on use (as the other exec workbooks do).
import type ExcelJS from 'exceljs'
import type { CompPerson, CompLine } from './CompsView'

const GREEN = 'FF0B3D2E'
const SUBHEAD = 'FFEAF2EE'
const MONEY = '£#,##0.00'
const INT = '#,##0'

function fill(cell: ExcelJS.Cell, argb: string) { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } } }
function header(ws: ExcelJS.Worksheet, row: number, labels: string[]) {
  const r = ws.getRow(row)
  labels.forEach((l, i) => { const c = r.getCell(i + 1); c.value = l; c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; fill(c, GREEN) })
  r.commit()
}
function fmtDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}
function fmtDateTime(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
}
function personName(p: CompPerson): string { return [p.surname, p.forename].filter(Boolean).join(', ') || `ID ${p.id_number}` }

export interface CompsWorkbookInput {
  season: string
  prevSeason: string
  generatedAt: string | null
  valueBasis: string | null
  matchedOnly: boolean
  people: CompPerson[]   // already filtered + sorted as on screen
  lines: CompLine[]      // every line of the season (filtered to the people shown)
}

export async function buildCompsWorkbook(input: CompsWorkbookInput): Promise<ExcelJS.Workbook> {
  const { default: Excel } = await import('exceljs')
  const wb = new Excel.Workbook()
  wb.creator = 'NYMR Reports'
  wb.created = new Date()

  const byPerson = new Map<number, CompLine[]>()
  for (const l of input.lines) { const a = byPerson.get(l.id_number) ?? []; a.push(l); byPerson.set(l.id_number, a) }

  // ---- Summary --------------------------------------------------------------
  {
    const ws = wb.addWorksheet('Summary')
    ws.columns = [{ width: 34 }, { width: 14 }, { width: 14 }, { width: 16 }]
    const t = ws.getCell(1, 1); t.value = `Comps — season ${input.season}`; ws.mergeCells(1, 1, 1, 4)
    t.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } }; fill(t, GREEN)
    ws.getCell(2, 1).value = `Generated ${input.generatedAt ? fmtDateTime(input.generatedAt) : '—'} · ${input.matchedOnly ? 'Matched users only' : 'All ID-card holders (matched and unmatched)'}`
    ws.getCell(2, 1).font = { italic: true, size: 9, color: { argb: 'FF6B6B6B' } }; ws.mergeCells(2, 1, 2, 4)

    header(ws, 4, ['By status', 'People', 'Comps', 'Value'])
    const order = ['Employee', 'Volunteer', 'Associate', 'Unknown']
    const statuses = [...order.filter((s) => input.people.some((p) => p.status === s)), ...[...new Set(input.people.map((p) => p.status))].filter((s) => !order.includes(s))]
    let row = 5
    let tp = 0, tc = 0, tv = 0
    for (const s of statuses) {
      const ps = input.people.filter((p) => p.status === s)
      const comps = ps.reduce((a, p) => a + p.comps, 0)
      const value = ps.reduce((a, p) => a + p.value, 0)
      ws.getCell(row, 1).value = s
      ws.getCell(row, 2).value = ps.length; ws.getCell(row, 2).numFmt = INT
      ws.getCell(row, 3).value = comps; ws.getCell(row, 3).numFmt = INT
      ws.getCell(row, 4).value = value; ws.getCell(row, 4).numFmt = MONEY
      tp += ps.length; tc += comps; tv += value; row++
    }
    const tr = ws.getRow(row)
    tr.getCell(1).value = 'Total'; tr.getCell(2).value = tp; tr.getCell(3).value = tc; tr.getCell(4).value = tv
    tr.getCell(2).numFmt = INT; tr.getCell(3).numFmt = INT; tr.getCell(4).numFmt = MONEY
    for (let c = 1; c <= 4; c++) { tr.getCell(c).font = { bold: true }; fill(tr.getCell(c), SUBHEAD) }
    row += 2
    const prevComps = input.people.reduce((a, p) => a + p.prev_comps, 0)
    ws.getCell(row, 1).value = `Comps used by these people in ${input.prevSeason}`; ws.getCell(row, 3).value = prevComps; ws.getCell(row, 3).numFmt = INT
    row += 2
    ws.getCell(row, 1).value = 'How a comp is valued'; ws.getCell(row, 1).font = { bold: true }; row++
    ws.getCell(row, 1).value = input.valueBasis ?? 'Standard Fare - Adult price in force on the session date'
    ws.mergeCells(row, 1, row, 4); ws.getCell(row, 1).alignment = { wrapText: true, vertical: 'top' }; ws.getRow(row).height = 42; row++
    ws.getCell(row, 1).value = 'Season = NYMR year (1 March – end February) of the date the comp was issued at the till, as the Info Hub Comps app counts them. A refunded comp counts −1.'
    ws.mergeCells(row, 1, row, 4); ws.getCell(row, 1).alignment = { wrapText: true, vertical: 'top' }; ws.getRow(row).height = 42
  }

  // ---- Sessions ranked -----------------------------------------------------
  {
    const ws = wb.addWorksheet('Sessions', { views: [{ state: 'frozen', ySplit: 1 }] })
    ws.columns = [{ width: 6 }, { width: 30 }, { width: 20 }, { width: 10 }, { width: 10 }, { width: 14 }]
    header(ws, 1, ['Rank', 'Session', 'Date & time', 'Comps', 'People', 'Value'])
    const bySession = new Map<string, { desc: string; at: string | null; comps: number; value: number; people: Set<number> }>()
    for (const l of input.lines) {
      const key = `${l.session_at ?? ''}|${l.session_desc ?? ''}`
      const s = bySession.get(key) ?? { desc: l.session_desc ?? '(no session)', at: l.session_at, comps: 0, value: 0, people: new Set<number>() }
      s.comps += l.qty; s.value += l.value; s.people.add(l.id_number); bySession.set(key, s)
    }
    const ranked = [...bySession.values()].sort((a, b) => b.comps - a.comps || (a.at ?? '').localeCompare(b.at ?? ''))
    ranked.forEach((s, i) => {
      const r = ws.getRow(i + 2)
      r.getCell(1).value = i + 1
      r.getCell(2).value = s.desc
      r.getCell(3).value = fmtDateTime(s.at)
      r.getCell(4).value = s.comps; r.getCell(4).numFmt = INT
      r.getCell(5).value = s.people.size; r.getCell(5).numFmt = INT
      r.getCell(6).value = s.value; r.getCell(6).numFmt = MONEY
    })
    ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: Math.max(2, ranked.length + 1), column: 6 } }
  }

  // ---- Users with drill-down ----------------------------------------------
  {
    const ws = wb.addWorksheet('Users', { views: [{ state: 'frozen', ySplit: 1 }] })
    ws.properties.outlineProperties = { summaryBelow: false, summaryRight: false }
    ws.columns = [{ width: 30 }, { width: 12 }, { width: 10 }, { width: 14 }, { width: 10 }, { width: 10 }, { width: 14 }, { width: 14 }, { width: 22 }, { width: 10 }]
    header(ws, 1, ['Name', 'Status', 'ID No', 'Card No', `Comps ${input.season}`, `Comps ${input.prevSeason}`, 'Last comp used', `Value ${input.season}`, 'Session / sale', 'Matched'])
    let row = 2
    for (const p of input.people) {
      const r = ws.getRow(row)
      r.getCell(1).value = personName(p); r.getCell(1).font = { bold: true }
      r.getCell(2).value = p.status
      r.getCell(3).value = p.id_number
      r.getCell(4).value = p.card_no ?? ''
      r.getCell(5).value = p.comps; r.getCell(5).numFmt = INT
      r.getCell(6).value = p.prev_comps; r.getCell(6).numFmt = INT
      r.getCell(7).value = fmtDate(p.last_used)
      r.getCell(8).value = p.value; r.getCell(8).numFmt = MONEY
      r.getCell(9).value = `${(byPerson.get(p.id_number) ?? []).length} comp line${(byPerson.get(p.id_number) ?? []).length === 1 ? '' : 's'} (click + to expand)`
      r.getCell(9).font = { italic: true, size: 9, color: { argb: 'FF6B6B6B' } }
      r.getCell(10).value = p.matched ? 'Yes' : 'No'
      for (let c = 1; c <= 10; c++) fill(r.getCell(c), SUBHEAD)
      row++
      const lines = (byPerson.get(p.id_number) ?? []).slice().sort((a, b) => (a.session_at ?? a.sale_at).localeCompare(b.session_at ?? b.sale_at))
      let sub = 0, subV = 0
      for (const l of lines) {
        const d = ws.getRow(row)
        d.getCell(1).value = `    ${l.session_desc ?? '(no session)'}`
        d.getCell(2).value = l.session_at ? fmtDateTime(l.session_at) : ''
        d.getCell(5).value = l.qty; d.getCell(5).numFmt = INT
        d.getCell(7).value = fmtDateTime(l.sale_at)
        d.getCell(8).value = l.value; d.getCell(8).numFmt = MONEY
        d.getCell(9).value = `issued ${fmtDateTime(l.sale_at)}`
        d.outlineLevel = 1; d.hidden = true
        sub += l.qty; subV += l.value; row++
      }
      const s = ws.getRow(row)
      s.getCell(1).value = `    Total for ${personName(p)}`; s.getCell(1).font = { italic: true }
      s.getCell(5).value = sub; s.getCell(5).numFmt = INT; s.getCell(5).font = { italic: true }
      s.getCell(8).value = subV; s.getCell(8).numFmt = MONEY; s.getCell(8).font = { italic: true }
      s.outlineLevel = 1; s.hidden = true
      row++
    }
    const tot = ws.getRow(row)
    tot.getCell(1).value = `Total (${input.people.length} people)`
    tot.getCell(5).value = input.people.reduce((a, p) => a + p.comps, 0); tot.getCell(5).numFmt = INT
    tot.getCell(6).value = input.people.reduce((a, p) => a + p.prev_comps, 0); tot.getCell(6).numFmt = INT
    tot.getCell(8).value = input.people.reduce((a, p) => a + p.value, 0); tot.getCell(8).numFmt = MONEY
    for (let c = 1; c <= 10; c++) { tot.getCell(c).font = { bold: true }; fill(tot.getCell(c), GREEN); tot.getCell(c).font = { bold: true, color: { argb: 'FFFFFFFF' } } }
  }

  return wb
}

export async function downloadCompsXlsx(input: CompsWorkbookInput): Promise<void> {
  const wb = await buildCompsWorkbook(input)
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `NYMR-Comps-${input.season.replace('/', '-')}${input.matchedOnly ? '' : '-all'}.xlsx`
  document.body.appendChild(a); a.click(); document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}
